import React, {useContext, useEffect, useState} from 'react'
import {
  TRADE_PARTNER_COUNTRY_CODE,
  CountrySelectComponent,
  AddressForm,
  COMPANY_TRADE_PARTNERS,
  TRADE_PARTNER_EXPECTED_FINANCING_LIMIT,
  TRADE_PARTNER_COMPANY_NAME,
  TRADE_PARTNER_LEGAL_FORM,
  SelectPureWithInputContainer,
  TRADE_PARTNER_STATE,
  TRADE_PARTNER_TRADING_CURRENCY,
} from '@modifi/plugin-onboarding'
import {
  Col,
  Row,
  AttentionIcon,
  withResponsiveHelper,
  DangerAlert,
  WarningAlert,
  InfoAlert,
  H6,
  P,
} from '@modifi/ui'
import {useTranslation} from '@modifi/plugin-i18n'
import {FormDataContext, FormValidationContext, useInputValue} from '@modifi/plugin-forms'
import {warning, alert} from '@modifi/colors'
import {Currency, CurrencyVal} from '@modifi/enums'

import {
  CustomState,
  CustomCity,
  CustomZipCode,
} from '../../../../../../features/trade-partner-custom-inputs'
import {FormContentWrapper} from '../../../../../../ui/form'
import {FormInputComponentCol} from '../../../../../../plugins/onboarding'
import {
  AddressFormContextProvider,
  FormChangeArgs,
} from '../../../../../seller-trade-partners/context'
import {
  COMPANY_ADDRESS_DEFAULT_FORMAT,
  COMPANY_ADDRESS_FORMATS,
} from '../../../../../seller-trade-partners/components/company-identification/constants'
import CompanyNameSearch from '../../../../../seller-trade-partners/components/company-name-search'
import {WarningCol} from '../../../styles'
import CountrySpecificState from '../../../../../../features/trade-partner-custom-inputs/CountrySpecificState'
import {countrySpecific} from '../../../../../seller-trade-partners/utils'
import {EXPECTED_FINANCING_LIMIT_CAP} from '../../../../../seller-trade-partners/constants'
import {getCurrenciesOptions} from '../../../../../../utils/getCurrenciesOptions'
import ExpectedFinancingLimit from '../../../../../seller-trade-partners/components/expected-financing-limit-amount'
import {userPreferences} from '../../../../../../utils/user-preferences'
import {preunderwritingSubmitStateKeyPrefix} from '../../../constants'

const getSelectedCountryBuyerRiskLevel = (
  selectedCountry: string | undefined,
  highBuyerRiskCountries: string[],
  mediumBuyerRiskCountries: string[]
): string | undefined => {
  switch (true) {
    case !selectedCountry || !highBuyerRiskCountries || !mediumBuyerRiskCountries:
      return undefined
    case highBuyerRiskCountries?.includes(selectedCountry!):
      return 'high'
    case mediumBuyerRiskCountries?.includes(selectedCountry!):
      return 'medium'
    default:
      return undefined
  }
}

export type BuyerFormContentProps = {
  externalTradePartnerId?: string
  highBuyerRiskCountries?: string[]
  onCompanySelect: (selected: boolean | string) => void
  canUpdateTradePartnerCompanyDetails?: boolean
  isTradePartnerDataInfoReadOnly: boolean
  externalErrorMessages?: Record<string, string>
  discardChangeCounter: number
  isSmOrSmaller?: boolean
  getLegalForms: (countryCode: string) => string[]
  fetchLegalForms: (countryCode: string) => void
  mediumBuyerRiskCountries?: string[]
  financingCurrencies: string[]
  getConversionRate: (currency: CurrencyVal) => number
  buyerCount: number
  externalCompanyId?: string
}

const BuyerFormContent: React.FC<BuyerFormContentProps> = ({
  externalTradePartnerId,
  highBuyerRiskCountries = [],
  onCompanySelect,
  canUpdateTradePartnerCompanyDetails = true,
  isTradePartnerDataInfoReadOnly,
  externalErrorMessages = {},
  discardChangeCounter,
  isSmOrSmaller = false,
  getLegalForms,
  fetchLegalForms,
  mediumBuyerRiskCountries = [],
  financingCurrencies,
  getConversionRate,
  buyerCount,
  externalCompanyId,
}) => {
  const {t} = useTranslation(['charon', 'plugin-forms'])
  const [isCompanySearch, setIsCompanySearch] = useState(!externalTradePartnerId)
  const [isCompanySelected, setIsCompanySelected] = useState(false)
  const [hasLegalFormValueFromSelection, setHasLegalFormValueFromSelection] = useState(false)
  const {value: selectedCountryCode} = useInputValue({name: TRADE_PARTNER_COUNTRY_CODE})
  const {value: selectedCompanyName} = useInputValue({name: TRADE_PARTNER_COMPANY_NAME})
  const {value: addressStateValue} = useInputValue({name: TRADE_PARTNER_STATE})
  const {value: tradingCurrency} = useInputValue({name: TRADE_PARTNER_TRADING_CURRENCY})
  const {value: expectedFinancingLimit} = useInputValue<number>({
    name: TRADE_PARTNER_EXPECTED_FINANCING_LIMIT,
  })
  const {changeData} = useContext(FormDataContext)
  const {triggerRevalidation} = useContext(FormValidationContext)

  const conversionRate =
    tradingCurrency !== Currency.USD ? getConversionRate(tradingCurrency as CurrencyVal) : 1
  const convertedExpectedFinancingLimit = +expectedFinancingLimit * conversionRate

  useEffect(() => {
    // setting usd by default for bsr creation
    if (selectedCompanyName && !externalTradePartnerId) {
      changeData(TRADE_PARTNER_TRADING_CURRENCY, Currency.USD)
      setTimeout(() => {
        // waiting for a second to trigger validation so that the address fields are loaded onto dom
        triggerRevalidation && triggerRevalidation(undefined)
      }, 1000)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCompanyName])

  const handleOnChange = ({valueSelected, dataEntry, hasLegalFormValue}: FormChangeArgs) => {
    if (dataEntry) {
      dataEntry === 'MANUAL' ? setIsCompanySearch(false) : setIsCompanySearch(true)
    }
    if (valueSelected !== undefined) {
      setIsCompanySelected(!!valueSelected)
      onCompanySelect && onCompanySelect(valueSelected)
      setHasLegalFormValueFromSelection(!!hasLegalFormValue)
    }
    if (valueSelected === false && isCompanySelected) {
      setIsCompanySelected(false)
      onCompanySelect && onCompanySelect(false)
    }
  }

  useEffect(() => {
    if (discardChangeCounter && !externalTradePartnerId) {
      setIsCompanySearch(!externalTradePartnerId)
      setIsCompanySelected(false)
    }
    // need to call only when discarded
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [discardChangeCounter])

  useEffect(() => {
    if (selectedCountryCode) {
      fetchLegalForms(selectedCountryCode as string)
      setIsCompanySelected(false)
      setIsCompanySearch(true)
    }
    //  need to only run when countryCode changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedCountryCode])

  useEffect(() => {
    if (
      selectedCountryCode &&
      ['US', 'CA'].includes(selectedCountryCode as string) &&
      addressStateValue &&
      isCompanySelected
    ) {
      setIsCompanySelected(false)
      setIsCompanySearch(true)
    }
    //  need to only run when addressStateValue changes
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addressStateValue])

  useEffect(() => {
    const preunderwritingSubmitStateKey = `${preunderwritingSubmitStateKeyPrefix}:${externalCompanyId}`
    if (!userPreferences.get(preunderwritingSubmitStateKey) && !buyerCount && externalCompanyId) {
      userPreferences.set(preunderwritingSubmitStateKey, 'false')
    }
  }, [buyerCount, externalCompanyId])

  const countryBuyerRiskLevel = getSelectedCountryBuyerRiskLevel(
    selectedCountryCode as string | undefined,
    highBuyerRiskCountries as string[],
    mediumBuyerRiskCountries as string[]
  )

  const showStateFieldBeforeSearch =
    selectedCountryCode && ['US', 'CA']?.includes(selectedCountryCode as string)

  const showCompanyNameSearch =
    (selectedCountryCode && !['US', 'CA']?.includes(selectedCountryCode as string)) ||
    addressStateValue

  const BuyerRiskCountryAlertComponent =
    countryBuyerRiskLevel === 'high' ? DangerAlert : WarningAlert

  return (
    <FormContentWrapper>
      <H6>{t(`intro-page.buyer-details.add-${buyerCount ? '' : 'first-'}buyer.headline`)}</H6>
      <P mb={24}>
        {t(`intro-page.buyer-details.add-${buyerCount ? '' : 'first-'}buyer.description`)}
      </P>

      {!externalTradePartnerId && (
        <Row alignItems="center">
          <FormInputComponentCol
            t={t}
            inputComponent={CountrySelectComponent}
            inputName={TRADE_PARTNER_COUNTRY_CODE}
            inputProps={{
              label: t('country-code.label', {ns: 'plugin-forms'}),
              disabled: !canUpdateTradePartnerCompanyDetails,
              labelAddition: isSmOrSmaller && countryBuyerRiskLevel && (
                <AttentionIcon color={countryBuyerRiskLevel === 'medium' ? warning : alert} />
              ),
            }}
            isValid={!externalErrorMessages[TRADE_PARTNER_COUNTRY_CODE]}
            errorMessage={externalErrorMessages[TRADE_PARTNER_COUNTRY_CODE]}
            colProps={{md: 7}}
          />
          {countryBuyerRiskLevel &&
            (isSmOrSmaller ? (
              <WarningCol alertColor={countryBuyerRiskLevel === 'high' ? alert : warning}>
                {t(`intro-page.country-${countryBuyerRiskLevel}-risk.option`)}
              </WarningCol>
            ) : (
              <Col md={5}>
                <BuyerRiskCountryAlertComponent>
                  {t(`intro-page.country-${countryBuyerRiskLevel}-risk.option`)}
                </BuyerRiskCountryAlertComponent>
              </Col>
            ))}
          {selectedCountryCode && ['US', 'CA']?.includes(selectedCountryCode as string) && (
            <FormInputComponentCol
              t={t}
              inputComponent={CountrySpecificState}
              inputName={TRADE_PARTNER_STATE}
              inputProps={{
                label: t(countrySpecific('state.label', selectedCountryCode as string), {
                  ns: 'plugin-forms',
                }),
                disabled: !canUpdateTradePartnerCompanyDetails,
              }}
              isValid={!externalErrorMessages[TRADE_PARTNER_STATE]}
              errorMessage={externalErrorMessages[TRADE_PARTNER_STATE]}
              colProps={{md: 8}}
            />
          )}
        </Row>
      )}
      <Row>
        <Col md={8}>
          <AddressFormContextProvider
            onChange={handleOnChange}
            selectedCountryCode={selectedCountryCode as string}
            discardChangeCounter={discardChangeCounter}
            preselectedState={addressStateValue as string}
          >
            <AddressForm
              formType={COMPANY_TRADE_PARTNERS!}
              isReadOnly={
                (isCompanySelected && !externalTradePartnerId) ||
                !canUpdateTradePartnerCompanyDetails
              }
              format={
                // @ts-expect-error
                selectedCountryCode && COMPANY_ADDRESS_FORMATS?.[selectedCountryCode]
                  ? // @ts-expect-error
                    COMPANY_ADDRESS_FORMATS?.[selectedCountryCode]
                  : COMPANY_ADDRESS_DEFAULT_FORMAT
              }
              components={
                !externalTradePartnerId
                  ? {
                      countryCode: () => null,
                      companyName: showCompanyNameSearch ? CompanyNameSearch : () => null,
                      zone: !showStateFieldBeforeSearch ? CustomState : () => null,
                      city: CustomCity,
                      postCode: CustomZipCode,
                      ...(isCompanySearch &&
                        !isCompanySelected && {
                          city: () => null,
                          zone: () => null,
                          postCode: () => null,
                          streetName: () => null,
                          streetNumber: () => null,
                        }),
                    }
                  : {zone: CustomState}
              }
            />
          </AddressFormContextProvider>
        </Col>
      </Row>
      {(isCompanySelected || selectedCompanyName) && (
        <>
          <Row>
            {!!selectedCountryCode && getLegalForms(selectedCountryCode as string)?.length > 0 && (
              <FormInputComponentCol
                t={t}
                inputComponent={SelectPureWithInputContainer}
                inputName={TRADE_PARTNER_LEGAL_FORM}
                inputProps={{
                  disabled:
                    (isCompanySelected && hasLegalFormValueFromSelection) ||
                    !canUpdateTradePartnerCompanyDetails,
                  required: true,
                }}
                options={
                  selectedCountryCode
                    ? getLegalForms(selectedCountryCode as string)?.map((legalForm: string) => ({
                        label: legalForm,
                        value: legalForm,
                      }))
                    : []
                }
                colProps={{md: 8}}
              />
            )}
          </Row>
          <Row>
            <FormInputComponentCol
              t={t}
              inputName={TRADE_PARTNER_TRADING_CURRENCY}
              colProps={{md: 8}}
              inputComponent={SelectPureWithInputContainer}
              options={getCurrenciesOptions(financingCurrencies || [])}
              inputProps={{
                disabled: isTradePartnerDataInfoReadOnly,
                required: true,
              }}
            />
          </Row>
          <Row>
            <ExpectedFinancingLimit
              canUpdateSellerTradePartnerDataInfo={!isTradePartnerDataInfoReadOnly}
            />
            {convertedExpectedFinancingLimit &&
            convertedExpectedFinancingLimit > EXPECTED_FINANCING_LIMIT_CAP.medium ? (
              <Col mt={-16} mb={24}>
                <InfoAlert>
                  {t(
                    `seller-trade-partner.${
                      convertedExpectedFinancingLimit > EXPECTED_FINANCING_LIMIT_CAP.high
                        ? 'high'
                        : 'medium'
                    }-expected-financing-limit.info-alert.message`
                  )}
                </InfoAlert>
              </Col>
            ) : null}
          </Row>
        </>
      )}
    </FormContentWrapper>
  )
}

export default withResponsiveHelper(BuyerFormContent)
